/**
 * `advisor-intake.clientWorkspace`: the link from a submitted intake to the sponsored Steward
 * workspace a firm set up from it (crossmarc/steward-app#1487, SCS 3.2; founder decisions on
 * steward-app#1016, 2026-09-16).
 *
 * ## Who writes it
 *
 * The façade's `sponsoredSetup.ts` writer, `replaceLink` — under the firm caller's own token,
 * with the same reconstruct-and-replace shape `PUT /intakes/{id}/status` uses: read the row,
 * spread its STORED attributes, replace this one key, write with the version just read. So a
 * status change keeps the link and a link write keeps the status. No value in it comes off a
 * request: the ids are appcore's response, the people are the caller's own `/me` identity, and
 * the times are the server's.
 *
 * ⚠️ **Two call sites since crossmarc/steward-app#1636 (2026-09-22), not one.** `replaceLink` is
 * exported and `GET /intakes` (`handlers/advisor.ts`'s `listIntakes`) calls it too, to reconcile
 * a stale `invited` link on a plain read once the family has joined — the SAME writer, so this
 * paragraph's guarantees (reconstruct-and-replace, version-checked, never touches `status`) hold
 * for that caller as well. What stays true of the ORIGINAL claim: nothing outside
 * `sponsoredSetup.ts`'s `replaceLink` writes this key directly: `listIntakes` reuses the
 * function rather than reimplementing the write.
 *
 * ## The states
 *
 *   - `invited`   — appcore set the workspace up and the family has not been seen to accept.
 *   - `active`    — the family accepted. Written only when the façade has READ that (an active
 *                   family-role member of the client workspace); never assumed.
 *   - `cancelled` — the firm cancelled before the family accepted. The link is kept, never
 *                   removed (second round, engineering adjustment), and the intake may be set up
 *                   again, which replaces it with a fresh `invited` link.
 *   - `ended`     — the firm ended an accepted sponsorship. The family keeps the workspace;
 *                   appcore refuses to set the same intake up again (`SOURCE_REF_ENDED`).
 *
 * ⚠️ **`invited` does not prove the family has NOT accepted.** Acceptance happens in the family's
 * session and nothing writes to the firm workspace then (the family holds no token there). The
 * façade moves a link to `active` when a route it runs learns otherwise, and appcore stays the
 * authority on what cancel, resend and end may do.
 */
import { z } from 'zod';
import { ClientPrefillCountsSchema } from './clientPrefillMessage.js';
import { ULID_PATTERN } from './intakeMessage.js';

export const INTAKE_CLIENT_WORKSPACE_STATES = ['invited', 'active', 'cancelled', 'ended'] as const;
export type IntakeClientWorkspaceState = (typeof INTAKE_CLIENT_WORKSPACE_STATES)[number];

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();

/**
 * What the façade did about PREFILL for this setup (crossmarc/steward-app#1491, SCS 4.3), written
 * once, right after it tried to enqueue the plan:
 *
 *   - `queued`            — the plan is on the client-prefill queue. **Not "done".** The writer does
 *                           not report back in v1 (the write-back is a follow-up), so completion is
 *                           visible only through its logs, its DLQ and that DLQ's alarm.
 *   - `failed-to-enqueue` — nothing was queued: the stored intake, its snapshot or its `submittedAt`
 *                           did not parse, planning threw, the queue is not configured, or the send
 *                           failed (a send that timed out may still have landed). Setup succeeded.
 *   - `too-large`         — the serialized plan was past `CLIENT_PREFILL_MESSAGE_MAX_BYTES`, so it
 *                           was not sent.
 *
 * `counts` are the PLAN's rows per type (zero for every type when there was no plan), not rows
 * written.
 */
export const INTAKE_CLIENT_PREFILL_STATES = ['queued', 'failed-to-enqueue', 'too-large'] as const;
export type IntakeClientPrefillState = (typeof INTAKE_CLIENT_PREFILL_STATES)[number];

export const IntakeClientPrefillSchema = z.strictObject({
  state: z.enum(INTAKE_CLIENT_PREFILL_STATES),
  at: instant,
  counts: ClientPrefillCountsSchema,
});
export type IntakeClientPrefill = z.infer<typeof IntakeClientPrefillSchema>;

export const IntakeClientWorkspaceLinkSchema = z.strictObject({
  workspaceId: id,
  tenantId: id,
  /** When this link (this setup) was first written. */
  linkedAt: instant,
  /** The appcore userId of the firm principal who set it up. */
  linkedBy: id,
  state: z.enum(INTAKE_CLIENT_WORKSPACE_STATES),
  /** The latest time an invitation for this setup was issued by resend. */
  lastInvitedAt: instant.optional(),
  cancelledAt: instant.optional(),
  cancelledBy: id.optional(),
  endedAt: instant.optional(),
  endedBy: id.optional(),
  /** Prefill, as the façade left it at setup. Absent on a link written before SCS 4.3. */
  prefill: IntakeClientPrefillSchema.optional(),
});

export type IntakeClientWorkspaceLink = z.infer<typeof IntakeClientWorkspaceLinkSchema>;

/**
 * Reads a stored link. `absent` when the row carries no `clientWorkspace` key at all;
 * `unreadable` when it carries one that does not parse. A consumer deciding whether a new setup
 * may start must treat `unreadable` as LIVE (refuse), never as absent.
 */
export function readIntakeClientWorkspaceLink(
  value: unknown,
): { kind: 'absent' } | { kind: 'unreadable' } | { kind: 'link'; link: IntakeClientWorkspaceLink } {
  if (value === undefined) return { kind: 'absent' };
  const parsed = IntakeClientWorkspaceLinkSchema.safeParse(value);
  return parsed.success ? { kind: 'link', link: parsed.data } : { kind: 'unreadable' };
}

/**
 * Has sponsored setup started for this intake? (crossmarc/steward-app#1577, founder ruling
 * 2026-09-21: the form closes once it has.)
 *
 * `clientWorkspace` is the one attribute sponsored setup writes (`handlers/sponsoredSetup.ts`,
 * `replaceLink`), it is written when setup BEGINS (state `invited`), and nothing else writes it.
 *
 * 🚨 **Closed for every value EXCEPT an absent one and a well-formed `cancelled` link**
 * (crossmarc/steward-app#1633). By state:
 *
 *   - absent: setup never started. Open.
 *   - `invited`, `active`: a setup in progress, or an account the family accepted. Closed.
 *   - `ended`: the firm ended an accepted sponsorship, and the family KEEPS the workspace, so a
 *     real account exists and appcore refuses to set the intake up again. Closed.
 *   - `cancelled`: the firm withdrew the setup before the family accepted, the workspace is gone,
 *     and the intake is a prospect again (the façade's cancel keeps the link, marked cancelled,
 *     rather than removing it). OPEN: the snapshot is the family's to keep updating, and the
 *     firm sees their changes. A later setup replaces the link with a fresh `invited` one, which
 *     closes the form again.
 *   - anything that does not parse as a link (`readIntakeClientWorkspaceLink` answers
 *     `unreadable`), INCLUDING a malformed value whose `state` reads `cancelled`: closed.
 *   - a `cancelled` link missing `cancelledAt` or `cancelledBy`: closed. The schema leaves both
 *     optional, but the façade's cancel (`cancelSponsoredSetup`) always writes both, so a
 *     cancelled link without them is not one it wrote. Only a link shaped like the façade's
 *     cancel reopens the form; everything else fails closed. ⚠️ The firm-side readers
 *     (`services/steward-app-api/src/handlers/sponsoredSetup.ts` setup/cancel/end,
 *     `src/advisor/sponsoredSetup.ts` `noActiveSetup`, the façade's `onboardingRequest.ts`) key on `state`
 *     alone, so such a link would read as cancelled there and closed here. That value is
 *     unreachable, because `cancelSponsoredSetup` always writes both fields, and the divergence
 *     fails closed. Do not "align" this check to the looser readers.
 *
 * ⚠️ This decides what a save does NOW. It does not reopen a draft already STAMPED
 * `setupStartedAt` while the setup was live: `services/intake-writer/src/draft.ts`'s
 * `writableDraft` refuses a stamped draft before this is read, and the reader shows it
 * read-only, so no save arrives to clear it. That residual is recorded on
 * crossmarc/steward-app#1633.
 *
 * 🚨 **Moved here from `services/intake-writer/src/draft.ts` (crossmarc/steward-app#1786),
 * where it originated, so the intake claim mailer (`services/steward-app-api`) can apply the
 * SAME predicate to a linked `advisor-intake` row's `clientWorkspace` instead of reimplementing
 * it.** `services/intake-writer` re-exports this name from `draft.ts` rather than defining its
 * own copy, so its own call sites (`submittedIntakeState`, the pre-submit re-check) are
 * unchanged. Semantics are unchanged by the move: same states, same absent/cancelled exception,
 * same fail-closed default. A change to this function now affects both the writer's own closed
 * check and the mailer's — that shared blast radius is the point, not a risk to design around;
 * see crossmarc/steward-app#1786 for why the mailer needed a SECOND signal beyond the draft's
 * own tier-1 `setupStartedAt`/`intakeMissingAt` stamps.
 */
export function setupHasStarted(intakeAttributes: Record<string, unknown>): boolean {
  const read = readIntakeClientWorkspaceLink(intakeAttributes['clientWorkspace']);
  if (read.kind === 'absent') return false;
  return !(read.kind === 'link' && read.link.state === 'cancelled'
    && read.link.cancelledAt !== undefined && read.link.cancelledBy !== undefined);
}

/**
 * Is `intakeIdCandidate` usable as the `advisor-intake` id a submitted draft points at? A ULID
 * check, applied BEFORE the candidate is used to build any request path (the same ordering
 * `services/intake-writer/src/draft.ts`'s `submittedIntakeState` and the intake claim mailer's
 * `defaultCheckDraftClosed` (`services/steward-app-api/src/intakeClaimConsumer.ts`) both need,
 * and now share, per crossmarc/steward-app#1786's delta review, F-A/F-C). A candidate that
 * fails this is treated as unusable by both callers, never as "not yet submitted" — a
 * malformed or absent id after a draft claims `status === 'submitted'` is a data problem, not
 * evidence setup has not started.
 */
export function usableSubmittedIntakeId(intakeIdCandidate: unknown): intakeIdCandidate is string {
  return typeof intakeIdCandidate === 'string' && ULID_PATTERN.test(intakeIdCandidate);
}

/**
 * Given an already-fetched `advisor-intake` row (or `undefined`, for "no row" — a 404, or a
 * confirmed-gone id), decides whether the submitted intake it names is `'missing'`, `'closed'`
 * or still `'open'` for the draft carrying `draftId`.
 *
 * Moved out of `services/intake-writer/src/draft.ts`'s `submittedIntakeState`
 * (crossmarc/steward-app#1786 delta review, F-A) into here so the intake claim mailer's tier 2
 * applies the IDENTICAL decision instead of a shorter version of its own — the mailer's
 * previous inline check skipped the deleted-row test and the back-pointer test entirely, so a
 * deleted `advisor-intake` row or one that had stopped pointing back at this draft both read as
 * merely "setup has not started" there, when the writer already treated either as `'missing'`
 * (closed). Three distinct reasons collapse to `'missing'` — no row, a row appcore marks
 * deleted, and a row that does not point back at `draftId` — because a caller that cannot trust
 * the row is in the same position regardless of why: it must not read the prospect's answers
 * off it and must not treat the pointer as live.
 *
 * `'missing'` and `'closed'` are DIFFERENT outcomes for the writer (`'missing'` stamps
 * `intakeMissingAt`, `'closed'` does not — see `submittedIntakeState`'s own docblock) but the
 * SAME outcome for the mailer, which has no equivalent of `'missing'` in its own three-way
 * {@link DraftClosedCheck} (`intakeClaimConsumer.ts`) and folds it into `'closed'`: either way
 * the stale pointer must not be followed. Each caller maps this return value to its own shape;
 * this function itself is agnostic to which caller is asking.
 */
export function submittedIntakeRowState(
  row: { status?: string; attributes: Record<string, unknown> } | undefined,
  draftId: string,
): 'missing' | 'closed' | 'open' {
  // A row appcore still returns but marks deleted is as gone as one it does not return.
  if (!row || row.status === 'deleted') return 'missing';
  // The intake must point BACK at this draft. A disagreement means one of the two is not what
  // it claims, and treating somebody else's record as this draft's continuation is the one
  // outcome this must never produce.
  if (row.attributes['draftId'] !== draftId) return 'missing';
  return setupHasStarted(row.attributes) ? 'closed' : 'open';
}
